import { useEffect } from "react";
import { Box, Users, Globe, ArrowRight } from "lucide-react";
import { setCanonical, setMetaTags, SpeakableSchema } from "@/components/seo/SchemaMarkup";

const STORAJINI_URL = "https://storajini.com";

export default function AboutPage() {
  useEffect(() => {
    setCanonical("/about");
    setMetaTags({
      title: "About Storajini Guide | Philippines Storage & Parking Resource",
      description: "Storajini Guide is your comprehensive resource for storage, parking, and moving solutions across the Philippines. A companion site to Storajini.com.",
    });
  }, []);

  return (
    <div data-testid="about-page">
      <SpeakableSchema selectors={["h1", ".speakable-intro"]} />
      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-amber-900 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4">About Storajini Guide</h1>
            <p className="text-lg text-gray-300 leading-relaxed">
              Your comprehensive resource for storage, parking, and moving solutions across the Philippines.
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Our Mission</h2>
          <div className="space-y-4 text-gray-600 leading-relaxed">
            <p>
              Storajini Guide exists to help Filipinos navigate the challenges of finding storage, parking, and managing moves across the Philippines.
            </p>
            <p>
              With rapid urbanization in cities like Manila, Cebu, and Davao, space is becoming increasingly precious. Many people struggle to find affordable storage for their belongings or reliable parking for their vehicles.
            </p>
            <p>
              We provide city-by-city guides, practical tips, and expert advice to help you make informed decisions. When you're ready to find actual storage or parking spaces, we connect you with{" "}
              <a href={STORAJINI_URL} className="text-amber-600 font-semibold hover:underline">Storajini.com</a>,
              the Philippines' peer-to-peer storage and parking marketplace.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-10 text-center">What We Do</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Box, title: "City Guides", text: "Detailed storage, parking, and moving guides for 150+ cities across the Philippines. Find localized tips and information." },
              { icon: Users, title: "Expert Content", text: "Practical blog posts and articles written to help you solve storage and space challenges in the Filipino context." },
              { icon: Globe, title: "Connect to Storajini", text: "When you're ready to find or list a space, we seamlessly connect you to Storajini.com, the trusted marketplace." },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-8 border border-gray-100">
                <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center mb-5">
                  <item.icon className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-amber-500">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Find Your Space?</h2>
          <p className="text-amber-100 text-lg mb-8">Visit Storajini.com to browse available storage and parking spaces across the Philippines.</p>
          <a
            href={`${STORAJINI_URL}/search`}
           
           
            data-testid="about-cta-btn"
            className="inline-flex items-center px-8 py-4 bg-white text-amber-600 font-bold rounded-lg hover:bg-gray-50 transition-colors"
          >
            Visit Storajini.com <ArrowRight className="ml-2 w-5 h-5" />
          </a>
        </div>
      </section>
    </div>
  );
}
